/**
 * The questions Praxis asks, as the server owns them. Every write answers with
 * the whole store, so the card reads one shape whatever it last did.
 */

import type {
  AskRequest,
  QuestionAnswer,
  QuestionStoreResponse,
} from "@patent-studio/api-types";
import { get, post, put } from "@/services/api-client";

/** The trigger a press asks at: one of the commits the card stands in front of. */
export type QuestionTrigger = AskRequest["trigger"];

function questionsPath(taskId: string): string {
  return `/tasks/${taskId}/questions`;
}

/** Every question of the task, the queues that asked them, each saying what it affects. */
export function readQuestions(taskId: string): Promise<QuestionStoreResponse> {
  return get<QuestionStoreResponse>(questionsPath(taskId));
}

/** Ask the trigger's questions: its queue asks while the worker runs the pass. */
export function askAt(
  taskId: string,
  trigger: QuestionTrigger,
): Promise<QuestionStoreResponse> {
  return post<QuestionStoreResponse>(`${questionsPath(taskId)}/queues`, {
    trigger,
  });
}

/** Ask the trigger's questions ahead of the press, so the press usually finds them asked. */
export function prepareAt(
  taskId: string,
  trigger: QuestionTrigger,
): Promise<null> {
  return post<null>(`${questionsPath(taskId)}/prepare`, { trigger });
}

/** Reply: hold the attorney's answer on the question until the card's button sends it. */
export function saveReply(
  taskId: string,
  questionId: string,
  draft: QuestionAnswer,
): Promise<QuestionStoreResponse> {
  return put<QuestionStoreResponse>(
    `${questionsPath(taskId)}/${questionId}/draft`,
    { draft },
  );
}

/** Skip: park the question, which stays open for later. */
export function skipQuestion(
  taskId: string,
  questionId: string,
): Promise<QuestionStoreResponse> {
  return post<QuestionStoreResponse>(
    `${questionsPath(taskId)}/${questionId}/park`,
    {},
  );
}

/** Answer the replies with their saved drafts and close the queue, in one write. */
export function clearQueue(
  taskId: string,
  queueId: string,
  replied: readonly string[],
): Promise<QuestionStoreResponse> {
  return post<QuestionStoreResponse>(
    `${questionsPath(taskId)}/queues/${queueId}/clear`,
    { replied },
  );
}

/** Answer each named question with its saved reply, together, in one write. */
export function submitAnswers(
  taskId: string,
  questionIds: readonly string[],
): Promise<QuestionStoreResponse> {
  return post<QuestionStoreResponse>(`${questionsPath(taskId)}/submit`, {
    question_ids: questionIds,
  });
}
